import { ServerInferRequest } from "@ts-rest/core";
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import {
    Spreadsheet,
    spreadsheetContract,
} from "@common/contracts/spreadsheet.contract";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const spreadsheetContractClient = getBackendAPIClient(spreadsheetContract);

export type SpreadsheetAccessParams = ServerInferRequest<
    typeof spreadsheetContract.getSpreadsheet
>["query"];

const spreadsheetQueryKeys = {
    all: () => ["spreadsheet"] as const,
    fetch: (spreadsheetId: string) =>
        [...spreadsheetQueryKeys.all(), spreadsheetId] as const,
};

export function useSpreadsheetQuery(
    spreadsheetId: string,
    accessParams: SpreadsheetAccessParams,
    options?: Partial<UseQueryOptions<Spreadsheet>>,
) {
    return useQuery({
        queryKey: spreadsheetQueryKeys.fetch(spreadsheetId),
        queryFn: async () => {
            const result = await spreadsheetContractClient.getSpreadsheet({
                params: {
                    id: spreadsheetId,
                },
                query: accessParams,
            });

            return result.body.spreadsheet;
        },
        ...options,
    });
}

export function useSpreadsheetUpdateMutation(
    spreadsheetId: string,
    accessParams: SpreadsheetAccessParams,
    onSuccess?: () => void | Promise<void>,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof spreadsheetContract.updateSpreadsheet
            >["body"],
        ) => {
            await spreadsheetContractClient.updateSpreadsheet({
                params: {
                    id: spreadsheetId,
                },
                query: accessParams,
                body,
            });
        },
        onMutate: (body) => {
            const previousData = queryClient.getQueryData<Spreadsheet>(
                spreadsheetQueryKeys.fetch(spreadsheetId),
            );

            if (previousData) {
                const clone = structuredClone(previousData);
                body.updates.forEach((update) => {
                    if (!clone[update.row]) {
                        clone[update.row] = {};
                    }
                    clone[update.row][update.column] = update.value;
                });
                queryClient.setQueryData(
                    spreadsheetQueryKeys.fetch(spreadsheetId),
                    clone,
                );
            }

            return previousData;
        },
        onError: (_, __, previousData) => {
            if (previousData) {
                queryClient.setQueryData(
                    spreadsheetQueryKeys.fetch(spreadsheetId),
                    previousData,
                );
            }
        },
        onSuccess,
    });
}
